import { formatHeldSince, getGateReadings, getPrinterFaults } from "@/components/call/access-controls/gate-status.utils";
import { useBarrierUpMinutes } from "@/components/call/access-controls/use-barrier-up-minutes";
import type { AccessControlGateStatusRenderProps } from "@EspaceParkQC/sdk-call-center";
import { ArrowDownToLine, ArrowUpToLine, Ban, Car, MapPin, Printer, TriangleAlert, Wifi, WifiOff } from "lucide-react";
import { useTranslation } from "react-i18next";

type GateStatusProps = Pick<AccessControlGateStatusRenderProps, "gateName" | "hasGate" | "status" | "isLoading" | "isUnavailable" | "supported"> & {
  /** Identifies the gate for the "up for at least" clock, which starts over when it changes. */
  gateKey: string;
  /** Names of the other gates read through the same device; their readings are indistinguishable from this gate's. */
  sharedWith: readonly string[];
};

/** The warning once the barrier has read up for a while. It counts from when this screen first saw it, hence "at least". */
export function BarrierUpWarning({ minutes }: { minutes: number }): React.ReactElement {
  const { t } = useTranslation("main");

  return (
    <div role="status" className="rounded-md border border-warning bg-warning/15 p-3 text-sm flex items-start gap-2">
      <TriangleAlert className="size-4 mt-0.5 shrink-0" aria-hidden="true" />
      {t("intercom.access-controls.gate-status.barrier-up-for", { minutes })}
    </div>
  );
}

/**
 * Says which gate the access controls act on and what state it is in.
 * The name is always shown when there is a gate, even when the status is unsupported or failing.
 */
export default function GateStatus({ gateName, hasGate, status, isLoading, isUnavailable, supported, gateKey, sharedWith }: GateStatusProps): React.ReactElement | null {
  const { t } = useTranslation("main");
  const base = "intercom.access-controls.gate-status";
  // `status` is undefined while loading and after a failed read: that is "unknown", and the clock starts over.
  const barrierUpMinutes = useBarrierUpMinutes(gateKey, supported && status ? getGateReadings(status).barrierUp : undefined);
  if (!hasGate) return null;

  const readings = status ? getGateReadings(status) : undefined;
  const printerFaults = getPrinterFaults(status?.printer);
  const heldSince = formatHeldSince(status?.hold?.since);

  return (
    <section className="rounded-lg border bg-card p-4 space-y-3 mb-4" aria-label={t(`${base}.acts-on`, { gateName })}>
      <div className="flex items-start gap-2">
        <MapPin className="size-5 mt-0.5 shrink-0 text-primary" aria-hidden="true" />
        <p className="text-base leading-snug">
          <span className="text-muted-foreground">{t(`${base}.acts-on-label`)} </span>
          <strong className="font-bold text-lg">{gateName}</strong>
        </p>
      </div>

      {supported && isLoading && <p className="text-sm text-muted-foreground">{t(`${base}.loading`)}</p>}

      {supported && isUnavailable && (
        <p className="text-sm text-muted-foreground flex items-center gap-2">
          <TriangleAlert className="size-4 shrink-0" aria-hidden="true" />
          {t(`${base}.unavailable`)}
        </p>
      )}

      {supported && status && (
        <>
          {status.hold && (
            <div role="status" className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm">
              <p className="font-semibold">{heldSince ? t(`${base}.held-since`, { time: heldSince }) : t(`${base}.held`)}</p>
              {status.hold.reason && <p className="text-muted-foreground">{t(`${base}.held-reason`, { reason: status.hold.reason })}</p>}
            </div>
          )}

          {readings && (
            <div className="space-y-1.5">
              <p className="text-sm font-medium flex items-center gap-2">
                {readings.barrierUp ? <ArrowUpToLine className="size-4 shrink-0" aria-hidden="true" /> : <ArrowDownToLine className="size-4 shrink-0" aria-hidden="true" />}
                {t(`${base}.${readings.barrierUp ? "barrier-up" : "barrier-down"}`)}
              </p>
              {barrierUpMinutes !== undefined && <BarrierUpWarning minutes={barrierUpMinutes} />}
              {sharedWith.length > 0 && <p className="text-xs text-muted-foreground">{t(`${base}.shared-device`, { gateName: sharedWith.join(", ") })}</p>}
              <p className="text-sm font-medium flex items-center gap-2">
                {readings.vehicleAtTerminal ? <Car className="size-4 shrink-0" aria-hidden="true" /> : <Ban className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />}
                {t(`${base}.${readings.vehicleAtTerminal ? "vehicle-present" : "vehicle-absent"}`)}
              </p>
            </div>
          )}

          <p className="text-sm flex items-center gap-2">
            {status.connected ? <Wifi className="size-4 shrink-0" aria-hidden="true" /> : <WifiOff className="size-4 shrink-0 text-destructive" aria-hidden="true" />}
            <span className={status.connected ? undefined : "font-semibold"}>{t(`${base}.${status.connected ? "terminal-connected" : "terminal-disconnected"}`)}</span>
          </p>

          {!status.connected && (
            <div role="status" className="rounded-md border border-warning bg-warning/15 p-3 text-sm flex items-start gap-2">
              <TriangleAlert className="size-4 mt-0.5 shrink-0" aria-hidden="true" />
              {t(`${base}.terminal-disconnected-warning`)}
            </div>
          )}

          {printerFaults.length > 0 && (
            <div role="status" className="rounded-md border border-warning bg-warning/15 p-3 text-sm space-y-1">
              {printerFaults.map((fault) => (
                <p key={fault} className="flex items-center gap-2">
                  <Printer className="size-4 shrink-0" aria-hidden="true" />
                  {t(`${base}.printer.${fault}`)}
                </p>
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
}
